import React, { useState, useEffect } from 'react';
import {
  Pane,
  Heading,
  TextInputField,
  Button,
  toaster,
  Card,
  Text
} from 'evergreen-ui';
import api from '../services/api';

const SUBFINDER_SERVICES = [
  { key: 'virustotal', label: 'VirusTotal' },
  { key: 'securitytrails', label: 'SecurityTrails' },
  { key: 'censys', label: 'Censys' },
  { key: 'shodan', label: 'Shodan' },
  { key: 'github', label: 'GitHub' },
  { key: 'binaryedge', label: 'BinaryEdge' },
  { key: 'certspotter', label: 'CertSpotter' }
];

function Profile() {
  const [apiKeys, setApiKeys] = useState({});
  const [deepseekKey, setDeepseekKey] = useState('');
  const [customHeaderName, setCustomHeaderName] = useState('');
  const [customHeaderValue, setCustomHeaderValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await api.get('/users/me/subfinder-keys');
      setApiKeys(response.data);
      const deepseekResponse = await api.get('/users/me/deepseek-key');
      setDeepseekKey(deepseekResponse.data?.deepseek_api_key || '');

      // Fetch custom header settings
      const userResponse = await api.get('/users/me');
      setCustomHeaderName(userResponse.data?.custom_header_name || '');
      setCustomHeaderValue(userResponse.data?.custom_header_value || '');
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toaster.danger('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSubfinderKeys = async () => {
    try {
      setSaving(true);
      await api.put('/users/me/subfinder-keys', apiKeys);
      toaster.success('API keys updated successfully');
    } catch (error) {
      console.error('Error saving API keys:', error);
      toaster.danger('Failed to update API keys');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDeepseekKey = async () => {
    try {
      setSaving(true);
      await api.put('/users/me/deepseek-key', { deepseek_api_key: deepseekKey });
      toaster.success('DeepSeek API key updated successfully');
    } catch (error) {
      console.error('Error saving DeepSeek API key:', error);
      toaster.danger('Failed to update DeepSeek API key');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomHeader = async () => {
    try {
      setSaving(true);
      await api.put('/users/me/custom-header', {
        custom_header_name: customHeaderName,
        custom_header_value: customHeaderValue
      });
      toaster.success('Custom header updated successfully');
    } catch (error) {
      console.error('Error saving custom header:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update custom header';
      toaster.danger(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (service, value) => {
    setApiKeys(prev => ({
      ...prev,
      [service]: value
    }));
  };

  if (loading) {
    return <Pane>Loading...</Pane>;
  }

  return (
    <Pane padding={16}>
      <Card 
        backgroundColor="white" 
        elevation={1} 
        padding={24} 
        marginBottom={32}
      >
        <Heading size={700} marginBottom={24}>Subfinder API Keys</Heading>
        
        {SUBFINDER_SERVICES.map(({ key, label }) => (
          <TextInputField
            key={key}
            label={label}
            placeholder={`Enter your ${label} API key`}
            value={apiKeys[key] || ''}
            onChange={e => handleChange(key, e.target.value)}
            type="password"
            marginBottom={16}
          />
        ))}

        <Button
          appearance="primary"
          onClick={handleSaveSubfinderKeys}
          isLoading={saving}
          marginTop={16}
        >
          Save API Keys
        </Button>
      </Card>
      <Card
        backgroundColor="white"
        elevation={1}
        padding={24}
        marginBottom={32}
      >
        <Heading size={700} marginBottom={24}>DeepSeek API Key</Heading>
        <TextInputField
          label="DeepSeek API Key"
          placeholder="Enter your DeepSeek API key"
          value={deepseekKey}
          onChange={e => setDeepseekKey(e.target.value)}
        />
        <Button
          appearance="primary"
          onClick={handleSaveDeepseekKey}
          isLoading={saving}
          marginTop={16}
        >
          Save DeepSeek API Key
        </Button>
      </Card>

      <Card
        backgroundColor="white"
        elevation={1}
        padding={24}
        marginBottom={32}
      >
        <Heading size={700} marginBottom={24}>Custom HTTP Header</Heading>
        <Pane marginBottom={16}>
          <Text size={400} color="muted">
            Configure a custom HTTP header to be included with all security scanning tools (HTTPX, Nuclei, FFUF).
            This can be used to identify your authorized bug bounty testing.
          </Text>
        </Pane>
        <TextInputField
          label="Header Name"
          placeholder="e.g., X-BugBounty-Tag or X-HackerOne-ID"
          value={customHeaderName}
          onChange={e => setCustomHeaderName(e.target.value)}
          hint="Alphanumeric characters, dashes, and underscores only"
          marginBottom={16}
        />
        <TextInputField
          label="Header Value"
          placeholder="e.g., your-program-identifier"
          value={customHeaderValue}
          onChange={e => setCustomHeaderValue(e.target.value)}
          hint="The value to send with the custom header"
          marginBottom={16}
        />
        <Button
          appearance="primary"
          onClick={handleSaveCustomHeader}
          isLoading={saving}
          marginTop={16}
        >
          Save Custom Header
        </Button>
      </Card>
    </Pane>
  );
}

export default Profile; 